import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useState } from 'react';
import { DateTime } from 'luxon';

import { UnumSignatureDto } from 'enrollment/queries';
import { PsButtonTuner, PsCheckboxTuner } from 'stencilSetup';
import { useApiMutation } from 'theSource/utils';
import { useConfigurationQuery } from 'theSource/queries';
import { ErrorResponseDto, SignApplicationResponseDto } from '../types';
import { ApplicationContainerStyled } from '../UnumStyled';

export interface UnumApplicationSignaturesProps {
  signatures: UnumSignatureDto[];
  orgBenefitId: string;
  signatureSigned: string | undefined;
  setSignatureSigned: Dispatch<SetStateAction<string | undefined>>;
}

export const UnumApplicationSignatures = ({
  signatures,
  orgBenefitId,
  signatureSigned,
  setSignatureSigned,
}: UnumApplicationSignaturesProps) => {
  const { t } = useTranslation('enrollment.plugins');
  const { configuration } = useConfigurationQuery();
  const apiHost = configuration?.apiHost;
  const [signaturesChecked, setSignaturesChecked] = useState<{
    [key: number]: boolean;
  }>({});

  const handleSignatureChange = (e: any, ApplicantIndex: number) => {
    if (e?.target?.tagName === 'INPUT') {
      signaturesChecked[ApplicantIndex] = signaturesChecked[ApplicantIndex]
        ? !signaturesChecked[ApplicantIndex]
        : true;
      setSignaturesChecked(signaturesChecked);
    }
  };

  const { mutate: updateSignature } = useApiMutation<
    SignApplicationResponseDto,
    ErrorResponseDto,
    {}
  >({
    queryKey: 'UNUM_ELECT',
    method: 'put',
    endpoint: `${apiHost}/v2/self_service/plugins/unum/ajax_sign_application/${orgBenefitId}`,
    onSuccess: (response) => {
      if (response.status === 'success') {
        let dt: DateTime | string = DateTime.fromJSDate(new Date());
        dt = `${dt.toISODate()} ${dt.toISOTime({ includeOffset: false })}`;
        setSignatureSigned(dt);
      }
    },
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const allChecked = () => {
    return Object.values(signaturesChecked).every(
      (signature) => signature === true
    );
  };

  const signPlan = () => {
    if (allChecked()) {
      updateSignature({});
    }
  };

  return (
    <ApplicationContainerStyled data-testid="unum-applicaiton-signatures">
      <div>
        {signatures.length > 1 ? (
          <div data-testid="unum-signatures-multiple-container">
            <h4>
              {t('unum.Application.signatureTitle')}
              <span>
                <span aria-hidden="true" role="presentation" className="red">
                  *
                </span>
                <span className="sr-only">{t('common:required')}</span>
              </span>
            </h4>
            <div>
              {signatures.map((signature, index) => (
                <span key={index} data-testid="unum-signature">
                  {signature.Signature['SignatureOwner']}:{' '}
                  <PsCheckboxTuner
                    data-testid={`unum-signature-checkbox-${index}`}
                    inputId={`unum-signature-checkbox-${index}`}
                    name="unum-signature-checkbox"
                    label={t('unum.Application.agree')}
                    onClick={(e) => handleSignatureChange(e, index)}
                    value="1"
                    required={true}
                    requiredWord={t('required')}
                  />
                </span>
              ))}
              <p>
                {!signatureSigned ? (
                  <span id="unum_signature">
                    <PsButtonTuner
                      level="primary"
                      label={t('unum.Application.signButton')}
                      data-testid="unum-sign-button"
                      buttonId="unum-sign-button"
                      disabled={allChecked()}
                      {...{ onClick: () => signPlan() }}
                    />
                  </span>
                ) : (
                  <span>{t('unum.Application.signed')}</span>
                )}
              </p>
            </div>
          </div>
        ) : (
          <div data-testid="unum-signatures-single">
            <h4>
              {signatures[0]['Signature']['SignatureOwner']}{' '}
              {t('unum.Application.oneSignatureTitle')}
              <span>
                <span aria-hidden="true" role="presentation" className="red">
                  *
                </span>
                <span className="sr-only">{t('common:required')}</span>
              </span>
            </h4>
            <div className="panel-body">
              <p>
                {signatures[0]['Signature']['SignatureOwner']}:{' '}
                {!signatureSigned ? (
                  <span id="unum_signature">
                    <PsButtonTuner
                      level="primary"
                      label={t('unum.Application.signButton')}
                      data-testid="unum-sign-button"
                      buttonId="unum-sign-button"
                      {...{ onClick: () => signPlan() }}
                    />
                  </span>
                ) : (
                  <span>{signatureSigned}</span>
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    </ApplicationContainerStyled>
  );
};
